// Pinned conversations + private chat names — the rules, pure (owner, 2026-10-21).
//
// « Each user can pin conversations and add names to them. Pinning will not
// affect other users' interface. » The rows are the signed-in user's own
// (worker routes/chatPrefs.ts); this file decides which row belongs to which
// conversation, the list order, and what the search box matches.
//
// ⚠️ ONE PERSON, TWO CHAT IDS. WhatsApp files her words under a `@lid` privacy
// id; anything we start ourselves is keyed `<phone>@c.us` (worker
// lib/chatAlias.ts). A pin is keyed on the PHONE JID whenever the number is
// known, so it follows her across both forms; a row saved while the number
// was still unknown is keyed on the chat id and is still found through it.
//
// ORDER: pinned first, the most recently pinned on top (like WhatsApp); the
// rest keep the order they already had. A pin does not jump when a message
// arrives — it stays where it was put.
//
// Guard: chatPrefs.test.ts
import type { ChatPref, ChatPrefSave } from '@/lib/chatPrefsApi';
import type { WhatsAppConversation } from '@/lib/api';

export const MAX_CUSTOM_NAME = 60;

/** `212600000000` from any shape of a Moroccan number (the worker's canonicalPhone). */
function canonicalDigits(phone: string | null | undefined): string | null {
  const d = String(phone ?? '').replace(/\D/g, '');
  if (d.length < 8) return null;
  if (d.startsWith('212')) return d;
  if (d.startsWith('0')) return `212${d.slice(1)}`;
  if (d.length === 9) return `212${d}`;
  return d;
}

/** The phone JID when the number is known — the key both forms share. */
export function chatKeyOf(chatId: string, phone?: string | null): string {
  const fromPhone = canonicalDigits(phone);
  if (fromPhone) return `${fromPhone}@c.us`;
  if (chatId.endsWith('@c.us')) {
    const fromId = canonicalDigits(chatId.slice(0, -'@c.us'.length));
    if (fromId) return `${fromId}@c.us`;
  }
  return chatId;
}

/** Every key a row for this conversation may sit under, canonical first. */
export function keysOf(chatId: string, phone?: string | null): string[] {
  return [...new Set([chatKeyOf(chatId, phone), chatId])];
}

/** The caller's rows that belong to this conversation, the canonical one first. */
export function prefsFor(prefs: ChatPref[], chatId: string, phone?: string | null): ChatPref[] {
  const keys = keysOf(chatId, phone);
  const canonical = keys[0];
  return prefs
    .filter((p) => keys.includes(p.chat_key) || p.chat_id === chatId)
    .sort((a, b) => Number(b.chat_key === canonical) - Number(a.chat_key === canonical)
      || b.updated_at.localeCompare(a.updated_at));
}

/** This conversation's pin and name for the caller, or null. */
export function prefFor(prefs: ChatPref[], chatId: string, phone?: string | null): ChatPref | null {
  return prefsFor(prefs, chatId, phone)[0] ?? null;
}

/** The name as it will be stored — the worker's cleanCustomName, mirrored. Empty → null. */
export function cleanCustomName(v: string | null | undefined): string | null {
  // eslint-disable-next-line no-control-regex
  const s = String(v ?? '').replace(/[\u0000-\u001f\u007f​-‏‪-‮⁦-⁩]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return null;
  return [...s].slice(0, MAX_CUSTOM_NAME).join('').trim() || null;
}

export type PrefChange = { pinned?: boolean; custom_name?: string | null };

/**
 * What to send, and what to show meanwhile (optimistic): the merged state of
 * this conversation after `change`, plus the caller's other rows to fold in.
 */
export function planSave(
  prefs: ChatPref[], chatId: string, phone: string | null | undefined, change: PrefChange, now = new Date(),
): { body: ChatPrefSave; next: ChatPref[] } {
  const mine = prefsFor(prefs, chatId, phone);
  const current = mine[0] ?? null;
  const key = chatKeyOf(chatId, phone);
  const pinned = change.pinned ?? Boolean(current?.pinned_at);
  const pinnedAt = pinned ? (mine.find((p) => p.pinned_at)?.pinned_at ?? now.toISOString()) : null;
  const customName = change.custom_name !== undefined ? cleanCustomName(change.custom_name) : (current?.custom_name ?? null);
  const body: ChatPrefSave = {
    chat_key: key, chat_id: chatId, pinned, pinned_at: pinnedAt, custom_name: customName,
    aliases: mine.map((p) => p.chat_key).filter((k) => k !== key),
  };
  const others = prefs.filter((p) => !mine.includes(p));
  const next = pinned || customName
    ? [...others, { chat_key: key, chat_id: chatId, pinned_at: pinnedAt, custom_name: customName, updated_at: now.toISOString() }]
    : others;
  return { body, next };
}

/** Pinned first (newest pin on top), the rest in the order they came. */
export function pinFirst<T extends { chat_id: string; phone?: string | null }>(rows: T[], prefs: ChatPref[]): T[] {
  if (!prefs.some((p) => p.pinned_at)) return rows;
  const pinnedAt = new Map<T, string>();
  for (const r of rows) {
    const at = prefFor(prefs, r.chat_id, r.phone)?.pinned_at;
    if (at) pinnedAt.set(r, at);
  }
  const pinned = rows.filter((r) => pinnedAt.has(r)).sort((a, b) => (pinnedAt.get(b) as string).localeCompare(pinnedAt.get(a) as string));
  return [...pinned, ...rows.filter((r) => !pinnedAt.has(r))];
}

/**
 * A pinned conversation stays on the list even once it is older than the
 * newest 300 messages the list is built from: a stand-in row that opens it.
 */
export function withPinnedMissing(rows: WhatsAppConversation[], prefs: ChatPref[]): WhatsAppConversation[] {
  const extra: WhatsAppConversation[] = [];
  for (const p of prefs) {
    if (!p.pinned_at) continue;
    const present = rows.some((r) => prefsFor([p], r.chat_id, r.phone).length > 0)
      || extra.some((r) => r.chat_id === p.chat_id);
    if (present) continue;
    const phone = p.chat_key.endsWith('@c.us') ? p.chat_key.slice(0, -'@c.us'.length) : null;
    extra.push({ chat_id: p.chat_id, name: null, phone, last_body: null, last_at: null, count: 0 });
  }
  return extra.length ? [...rows, ...extra] : rows;
}

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[’']/g, ' ').toLowerCase();

/** Does the caller's private name for this conversation match what was typed? */
export function matchesCustomName(pref: ChatPref | null | undefined, query: string): boolean {
  const q = query.trim();
  if (q.length < 2 || !pref?.custom_name) return false;
  return fold(pref.custom_name).includes(fold(q));
}
